import type { ReactNode } from "react"

import type { ToastInput } from "@/components/motion/animated-toast-stack"

// The toast surface is beUI's AnimatedToastStack; this module keeps the
// pre-existing singleton API — toast() callable from anywhere, useToast() for
// callers that want it off a hook — so no call site changed. Toasts fired
// before a <Toaster> mounts are queued and flushed on registration: pages
// pre-dispatch toasts (tests do exactly that) and a data-load effect can run
// before the Toaster sibling's effect in the same commit.
export type ToastParams = {
  title: ReactNode
  description?: ReactNode
  variant?: "default" | "destructive"
  /** Override the auto-dismiss window; omit for the Toaster's default. */
  duration?: number
}

type Dispatcher = (input: ToastInput) => string

let dispatcher: Dispatcher | null = null
const pending: ToastInput[] = []

export function registerToastDispatcher(next: Dispatcher | null) {
  dispatcher = next
  if (next) {
    while (pending.length > 0) next(pending.shift() as ToastInput)
  }
}

export function toast(params: ToastParams) {
  const input: ToastInput = {
    title: params.title,
    description: params.description,
    status: params.variant === "destructive" ? "error" : "neutral",
    duration: params.duration,
  }
  if (dispatcher) return dispatcher(input)
  pending.push(input)
  return null
}

export function useToast() {
  return { toast }
}
